/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import { Fragment, useState, useEffect } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { find } from 'lodash'
import { useNavigate } from 'react-router-dom'

import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormHelpText,
  EuiText,
  EuiButtonEmpty,
  EuiButtonIcon,
  EuiSpacer,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { DeploymentSearchResponse } from '@modules/cloud-api/v1/types'
import type { DeploymentStatus } from '@modules/ui-types'
import { CuiTable } from '@modules/cui/Table'
import type { CuiTableColumn } from '@modules/cui/Table'
import PrivacySensitiveContainer from '@modules/cui/PrivacySensitiveContainer'

import { deploymentUrl } from '@/lib/urlBuilder'
import DeploymentActionsPopover from '@/components/StackDeploymentSearch/DeploymentsTable/DeploymentActionsPopover'
import DeploymentInfo from '@/components/StackDeploymentSearch/DeploymentsTable/DeploymentInfo'
import { getDeploymentStatus, isPending } from '@/lib/stackDeployments/status'
import { getDeploymentHealthProblems } from '@/lib/healthProblems'
import { sortDeploymentsByRamAndStatus } from '@/lib/stackDeployments/sorting'
import KibanaStatusPopover from '@/components/Portal/components/PortalDeployments/PortalLinks/KibanaStatusPopover'
import ExternalLink from '@/components/ExternalLink'
import { getDeploymentMemoryCapacity } from '@/lib/stackDeployments/selectors/instances'
import { getDeploymentResourceEndpoint } from '@/lib/stackDeployments/selectors/endpoints'
import { hasOngoingConfigurationChange } from '@/lib/stackDeployments/selectors/configurationChanges'
import {
  getVersion,
  getRegionId,
  getFirstResourceType,
} from '@/lib/stackDeployments/selectors/fundamentals'
import { getDisplayName, getDisplayId } from '@/lib/stackDeployments/selectors/display'

import DeploymentRegion from '../Deployment/DeploymentRegion'
import CustomPagination from '../CustomPagination'
import { renderMatchSummary } from '../utils/renderMatchSummary'

import type { SortState, TableChangeState } from '../types'

type Props = {
  deployments: DeploymentSearchResponse[]
  onDeploymentAction: () => void
  pageSize: number | null
  shownCount?: number | null
  matchCount?: number | null
  pageIndex?: number
  hasNextPage?: boolean
  onTableChange?: (state: TableChangeState) => void
  tableSort?: SortState | null
  isSorting: boolean
  cursorHistory?: string[]
  isLoading?: boolean
}

type DeploymentWithPendingUpdate = {
  id?: string
  status?: DeploymentStatus
}

const messages = defineMessages({
  status: {
    id: `deployments-table.status-label`,
    defaultMessage: `Status`,
  },
  name: {
    id: `deployments-table.name-label`,
    defaultMessage: `Deployment name`,
  },
  version: {
    id: `deployments-table.version-label`,
    defaultMessage: `Version`,
  },
  configuration: {
    id: `deployments-table.configuration-label`,
    defaultMessage: `Configuration`,
  },
  stack: {
    id: `deployments-table.stack-label`,
    defaultMessage: `Stack`,
  },
  region: {
    id: `deployments-table.region-label`,
    defaultMessage: `Cloud region`,
  },
  totalStorage: {
    id: `deployments-table.storage-label`,
    defaultMessage: `Total storage`,
  },
  totalRam: {
    id: `deployments-table.ram-label`,
    defaultMessage: `Total RAM`,
  },
  totalCpu: {
    id: `deployments-table.cpu-label`,
    defaultMessage: `Total CPU`,
  },
  autoscaling: {
    id: `deployments-table.autoscaling-label`,
    defaultMessage: `Autoscaling`,
  },
  links: {
    id: `deployments-table.links-label`,
    defaultMessage: `Links`,
  },
  management: {
    id: 'deployments-table.manage-label',
    defaultMessage: 'Deployment management',
  },
})

const getDefaultState = (): DeploymentWithPendingUpdate => ({
  id: undefined,
  status: undefined,
})

const DeploymentsTable: React.FC<Props> = ({
  deployments,
  pageSize,
  shownCount,
  matchCount,
  pageIndex = 0,
  hasNextPage = false,
  onTableChange,
  cursorHistory = [],
  isLoading = false,
  tableSort,
  isSorting,
  onDeploymentAction: onDeploymentActionProp,
}) => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const [deploymentWithPendingUpdate, setDeploymentWithPendingUpdate] =
    useState<DeploymentWithPendingUpdate>(getDefaultState())

  useEffect(() => {
    if (deploymentWithPendingUpdate.id) {
      const deployment = find(deployments, { id: deploymentWithPendingUpdate.id })

      if (deployment) {
        const [problems] = getDeploymentHealthProblems({ deployment, eolStatus: null })

        if (isPending(problems, hasOngoingConfigurationChange({ deployment }))) {
          setDeploymentWithPendingUpdate(getDefaultState())
        }
      }
    }
  }, [deploymentWithPendingUpdate, deployments])

  const renderDeploymentLink = (id: string, displayName: string, kibanaLinkUrl?: string) => {
    if (!kibanaLinkUrl) {
      return <EuiButtonEmpty href={deploymentUrl(id)}>{displayName}</EuiButtonEmpty>
    }

    return <ExternalLink href={kibanaLinkUrl}>{displayName}</ExternalLink>
  }

  const renderDeploymentName = (deployment: DeploymentSearchResponse) => {
    const { id } = deployment
    const displayName = getDisplayName({ deployment })
    const displayId = getDisplayId({ deployment })
    const kibanaLinkUrl = getDeploymentResourceEndpoint({
      deployment,
      sliderInstanceType: 'kibana',
    })

    if (!kibanaLinkUrl) {
      return <KibanaStatusPopover deploymentId={deployment.id} displayName={displayName} />
    }

    return (
      <Fragment>
        {renderDeploymentLink(id, displayName, kibanaLinkUrl)}
        {displayId !== displayName && <EuiFormHelpText>{displayId}</EuiFormHelpText>}
      </Fragment>
    )
  }

  const handleDeploymentAction = (deployment: DeploymentSearchResponse) => {
    setDeploymentWithPendingUpdate({
      id: deployment.id,
      status: getDeploymentStatus({ deployment }),
    })
    onDeploymentActionProp()
  }

  const nameColumn = {
    id: 'name',
    label: formatMessage(messages.name),
    render: (deployment: DeploymentSearchResponse) => (
      <PrivacySensitiveContainer className='deployments-table-privacy-container'>
        {renderDeploymentName(deployment)}
      </PrivacySensitiveContainer>
    ),
    sortKey: [
      (deployment: DeploymentSearchResponse) => getDisplayName({ deployment }),
      (deployment: DeploymentSearchResponse) => getDisplayId({ deployment }),
    ],
    textOnly: false,
    width: '270px',
  }

  const columns: Array<CuiTableColumn<DeploymentSearchResponse>> = [
    nameColumn,

    {
      id: 'status',
      label: <FormattedMessage id='deployments-table.status' defaultMessage='Status' />,
      render: (deployment: DeploymentSearchResponse) => {
        const { id } = deployment

        return (
          <DeploymentInfo
            data-test-id='deployments-table-status'
            deployment={deployment}
            deploymentInfoItem='status'
            hasPendingUpdate={deploymentWithPendingUpdate?.id === id}
          />
        )
      },
      textOnly: false,
      width: '175px',
      sortKey: (deployment: DeploymentSearchResponse) => {
        const deploymentHealth = getDeploymentStatus({ deployment })
        return deploymentHealth.status
      },
    },

    {
      id: 'version',
      label: formatMessage(messages.version),
      sortKey: (deployment: DeploymentSearchResponse) => getVersion({ deployment }),
      render: (deployment: DeploymentSearchResponse) => {
        const version = getVersion({ deployment })

        if (version === null) {
          return null
        }

        return (
          <EuiText size='s' data-test-id='deployment-version'>
            {version}
          </EuiText>
        )
      },
      textOnly: false,
      width: `75px`,
    },

    {
      id: 'ram',
      label: formatMessage(messages.totalRam),
      render: (deployment: DeploymentSearchResponse) => (
        <EuiText size='s'>
          <DeploymentInfo deployment={deployment} deploymentInfoItem='ram' />
        </EuiText>
      ),
      textOnly: false,
      width: `75px`,
      sortKey: (deployment: DeploymentSearchResponse) =>
        getDeploymentMemoryCapacity({ deployment }),
    },

    {
      id: 'region',
      label: formatMessage(messages.region),
      render: (deployment: DeploymentSearchResponse) => (
        <EuiText size='s'>
          <DeploymentRegion regionId={getRegionId({ deployment })!} />
        </EuiText>
      ),
      sortKey: [(deployment: DeploymentSearchResponse) => getRegionId({ deployment })],
      textOnly: false,
      width: '125px',
    },

    {
      label: formatMessage(messages.links),
      render: (deployment: DeploymentSearchResponse) => (
        <EuiFlexGroup
          alignItems='center'
          justifyContent='flexEnd'
          gutterSize='s'
          responsive={false}
        >
          <EuiFlexItem grow={false}>
            <EuiButtonIcon
              onClick={() => navigate(deploymentUrl(deployment.id))}
              iconType='gear'
              className='deployments-table-management-link'
              aria-label={formatMessage(messages.management)}
            />
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <DeploymentActionsPopover
              deployment={deployment}
              onDeploymentAction={() => handleDeploymentAction(deployment)}
            />
          </EuiFlexItem>
        </EuiFlexGroup>
      ),
      textOnly: false,
      width: '50px',
      align: 'center',
    },
  ]

  let rows = deployments || []

  // Apply default sorting (RAM & status) only when no explicit table sort is active
  if (!tableSort) {
    rows = sortDeploymentsByRamAndStatus(rows)
  }

  // Decide whether to delegate sorting of certain columns to the backend.
  // If the number of rows is smaller than the page size, local (frontend) sorting is sufficient
  // and avoids unnecessary backend round trips.
  const effectivePageSize = pageSize || 30
  const backendSortableColumns =
    matchCount && matchCount >= effectivePageSize ? ['name', 'version'] : undefined

  const renderPagination = ({
    activePage: _activePage,
    pageCount: _pageCount,
    onChangePage: _onChangePage,
  }) => {
    // Calculate total pages once
    const totalPages = matchCount && pageSize ? Math.ceil(matchCount / pageSize) : null

    // Use totalPages for reliable pagination logic
    const showPagination = totalPages !== null && totalPages > 1
    const showMatchSummary = shownCount !== null && matchCount !== null

    if (!showMatchSummary && !showPagination) {
      return null
    }

    return (
      <Fragment>
        <EuiSpacer size='m' />

        <EuiFlexGroup alignItems='center' justifyContent='spaceBetween'>
          <EuiFlexItem grow={false}>
            {showMatchSummary && (
              <EuiFormHelpText>
                {renderMatchSummary({
                  page: pageIndex,
                  currentPageSize: deployments.length,
                  shownCount: shownCount || null,
                  matchCount: matchCount || null,
                  pageSize,
                  totalPages,
                })}
              </EuiFormHelpText>
            )}
          </EuiFlexItem>

          {showPagination && (
            <EuiFlexItem grow={false}>
              <CustomPagination
                aria-label='Deployment pagination'
                pageCount={totalPages}
                activePage={pageIndex}
                hasNextPage={hasNextPage}
                cursorHistory={cursorHistory}
                isLoading={isLoading}
                onPageClick={(page) => {
                  if (onTableChange) {
                    onTableChange({ page: { index: page } })
                  }
                }}
              />
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </Fragment>
    )
  }

  return (
    <CuiTable<DeploymentSearchResponse>
      rows={rows}
      columns={columns}
      pageSize={effectivePageSize}
      className='stack-deployment-list-table'
      renderPagination={renderPagination}
      backendSortableColumns={backendSortableColumns}
      // Show loading skeleton while a backend sort request is in flight
      initialLoading={isSorting}
      onSortChange={({ key, direction }) => {
        onTableChange?.({ sort: { field: String(key), direction }, page: { index: 0 } })
      }}
      getRowTestSubj={(deployment) => `${getFirstResourceType({ deployment })}-row`}
      getRowId={(deployment) => deployment.id}
      data-test-id='stack-deployment-list-table'
    />
  )
}

export default DeploymentsTable
